// components/CustomerOverview.js
import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, ArrowRight, Users, Phone, Search, X, Clock, Settings, ChevronLeft, ChevronRight, ChevronDown, CalendarCheck, Tag, MessageCircle, ArrowUpDown, SortAsc, Calendar, Video, Headphones, BookOpen, Star, ClipboardCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getCustomerStages } from '../utils/customerStages';
import { formatMoney, generateDeviceId } from '../utils/deviceUtils';
import { useCustomerHistory } from '../hooks/useCustomerHistory';
import { formatKoreanTimeUserFriendly } from '../utils/timeUtils';
import { listContainerVariants, listItemVariants, buttonVariants } from '../utils/animationUtils';
import { useActionLog } from '../hooks/useActionLog';
import { calculateRemainingDays } from '../utils/trialUtils';
import { getActiveAudiobook, incrementAudiobookPlay } from '../firebase';
import { idbGetSetting, idbSetSetting, openDb } from '../utils/idb';
const HistoryModal = React.lazy(() => import('./HistoryModal'));
const AudiobookPlayerModal = React.lazy(() => import('./AudiobookPlayerModal'));
const FavoriteModal = React.lazy(() => import('../modals/FavoriteModal'));

// 성능 최적화된 스테이지 리스트 (떨림 방지 + 성능 유지)
const OptimizedStageList = ({ stageId, customers, visibleCounts, onLoadMore, onItemClick, sortByName = true, selectionMode, selectedIds, onToggleSelection }) => {
    const containerRef = useRef(null);
    const loadingMoreRef = useRef(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);

    const maxVisible = (visibleCounts && Object.prototype.hasOwnProperty.call(visibleCounts, stageId)) ? visibleCounts[stageId] : 50;

    // 검색 함수
    const performSectionSearch = useCallback(async (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const { idbGetAllCustomers } = await import('../utils/idb');
            const allCustomers = await idbGetAllCustomers();

            const normalize = (str) => (str || '').toString().trim().toLowerCase();
            const normalizedQuery = normalize(query);

            const results = allCustomers.filter(customer => {
                // 해당 섹션의 고객만 검색
                if (customer.stage !== stageId) return false;

                const searchFields = [
                    customer.name,
                    customer.phone,
                    customer.company,
                    customer.address,
                    customer.detailAddress,
                    customer.relation,
                    customer.memo,
                    customer.keyword
                ];

                return searchFields.some(field => normalize(field).includes(normalizedQuery));
            });

            setSearchResults(results);
        } catch (error) {
            console.error('섹션 검색 실패:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [stageId]);

    // 검색어 변경 시 검색 실행
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            performSectionSearch(searchQuery);
        }, 300); // 300ms 디바운싱

        return () => clearTimeout(timeoutId);
    }, [searchQuery, performSectionSearch]);

    // 정렬된 고객 목록 생성
    const sortedCustomers = useMemo(() => {
        const customersToSort = searchQuery.trim() ? searchResults : customers;

        if (!sortByName) return customersToSort;

        return [...customersToSort].sort((a, b) => {
            const nameA = (a.name || '').trim();
            const nameB = (b.name || '').trim();
            return nameA.localeCompare(nameB, 'ko', { numeric: true });
        });
    }, [customers, searchResults, searchQuery, sortByName]);

    const displayCustomers = sortedCustomers.slice(0, maxVisible);

    // 검색어 하이라이트 함수
    const highlightText = useCallback((text) => {
        if (!searchQuery.trim()) return text;

        const normalize = (str) => (str || '').toString().trim().toLowerCase();
        const normalizedQuery = normalize(searchQuery);
        const normalizedText = normalize(text);

        if (!normalizedText.includes(normalizedQuery)) return text;

        const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = String(text).split(regex);

        return parts.map((part, index) => {
            if (normalize(part) === normalizedQuery) {
                return <mark key={index} className="bg-yellow-200 text-yellow-900 rounded px-0.5">{part}</mark>;
            }
            return part;
        });
    }, [searchQuery]);

    // 스크롤 이벤트 최적화: 디바운싱으로 떨림 방지
    const onScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const nearEnd = scrollTop + clientHeight >= scrollHeight - 100;

        // 무한 스크롤 트리거
        if (nearEnd && !loadingMoreRef.current && maxVisible < customers.length) {
            loadingMoreRef.current = true;
            Promise.resolve(onLoadMore && onLoadMore(stageId))
                .catch(() => { })
                .finally(() => {
                    setTimeout(() => { loadingMoreRef.current = false; }, 200);
                });
        }

        // 스크롤 상태 관리 (떨림 방지)
        if (!isScrolling) {
            setIsScrolling(true);
        }

        // 스크롤 종료 감지
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
            setIsScrolling(false);
        }, 150);
    };

    // 메모이제이션으로 불필요한 리렌더링 방지
    const customerItems = useMemo(() =>
        displayCustomers.map((customer, index) => (
            <motion.div
                key={`${stageId}-${customer.id}`}
                className={`group/customer bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all duration-300 cursor-pointer ${isScrolling ? 'pointer-events-none' : ''
                    } ${selectionMode && selectedIds.has(customer.id) ? 'ring-2 ring-purple-500 bg-purple-50' : ''}`}
                onClick={() => {
                    if (isScrolling) return;
                    if (selectionMode) {
                        onToggleSelection(customer.id);
                    } else {
                        onItemClick(customer);
                    }
                }}
                variants={listItemVariants}
                initial="hidden"
                animate="visible"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                layout
            >
                <div className="flex items-start justify-between gap-3">
                    {selectionMode && (
                        <div className="flex items-center justify-center pt-1">
                            <input
                                type="checkbox"
                                checked={selectedIds.has(customer.id)}
                                onChange={() => onToggleSelection(customer.id)}
                                className="w-5 h-5 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-bold text-gray-800 text-lg">{highlightText(customer.name)}</h4>
                            {customer.priority === 'high' && (
                                <span className="bg-gradient-to-r from-red-500 to-red-600 text-white px-2 py-1 rounded-lg text-xs font-bold shadow-md">우선</span>
                            )}
                            {stageId === 'preparation' && customer.recommended && (
                                <span className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-2 py-1 rounded-lg text-xs font-bold shadow-md">추천</span>
                            )}
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                            {customer.value > 0 && (
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-green-600">{formatMoney(customer.value)}</span>
                                </div>
                            )}
                            {customer.memo && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mt-2">
                                    <div
                                        className="text-blue-700 text-sm whitespace-pre-line overflow-hidden"
                                        style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            lineHeight: '1.4',
                                            maxHeight: '2.8em'
                                        }}
                                    >
                                        {highlightText(customer.memo)}
                                    </div>
                                    {(() => {
                                        const lines = customer.memo.split('\n');
                                        if (lines.length > 2) {
                                            return (
                                                <div className="text-blue-500 text-xs mt-1 font-medium">
                                                    +{lines.length - 2}줄 더
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="text-gray-400 text-xs">터치</div>
                </div>
            </motion.div>
        )), [displayCustomers, isScrolling, highlightText, onItemClick, stageId, selectionMode, selectedIds, onToggleSelection]
    );

    return (
        <div>
            {/* 검색 입력창 */}
            <div className="mb-3 px-1">
                <div className="relative">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="이 섹션에서 고객 검색..."
                        className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                        {isSearching && (
                            <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        )}
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="p-1 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                        <Search className="w-4 h-4 text-gray-400" />
                    </div>
                </div>
                {searchQuery && (
                    <div className="mt-1 text-xs text-gray-500">
                        {searchResults.length}명의 고객을 찾았습니다
                    </div>
                )}
            </div>

            <motion.div
                className="max-h-80 overflow-y-auto space-y-3 p-1 scroll-smooth"
                ref={containerRef}
                onScroll={onScroll}
                style={{
                    // 하드웨어 가속으로 부드러운 스크롤
                    transform: 'translateZ(0)',
                    willChange: 'scroll-position'
                }}
                variants={listContainerVariants}
                initial="hidden"
                animate="visible"
            >
                {customerItems}
                {maxVisible < customers.length && (
                    <div className="text-center py-2 text-sm text-gray-500">
                        더 많은 고객을 보려면 스크롤하세요 ({maxVisible}/{customers.length})
                    </div>
                )}
            </motion.div>
        </div>
    );
};

// 히스토리 섹션을 별도 컴포넌트로 분리하여 재렌더링 시 상태가 초기화되지 않도록 함
const CustomerHistorySection = ({ customerId, highlightQuery = '', appStatus, customer }) => {
    const { sendActionLog } = useActionLog();
    const { history, addHistory, updateHistory, deleteHistory } = useCustomerHistory(customerId);
    const [note, setNote] = useState('');
    const [type, setType] = useState('note');
    const [followUpContent, setFollowUpContent] = useState('');
    const [followUpDate, setFollowUpDate] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editNote, setEditNote] = useState('');
    const [editType, setEditType] = useState('note');
    const [editFollowUpContent, setEditFollowUpContent] = useState('');
    const [editFollowUpDate, setEditFollowUpDate] = useState('');

    // 만료 상태일 때 업그레이드 유도
    const handleExpiredAction = () => {
        try {
            window.dispatchEvent(new Event('openApprovalModal'));
        } catch (e) {
            console.warn('[CustomerHistorySection] dispatch failed', e);
            alert('체험 기간이 만료되었습니다. 계속 사용하려면 사용신청을 해주세요.');
        }
    };

    // 만료 상태일 때 제한 초과 알림
    const handleLimitExceeded = async () => {
        const { getExpiredCustomerCount } = await import('../utils/expiredActionLimit');
        const count = await getExpiredCustomerCount();
        alert(`체험 기간이 만료되어 누적 10명까지만 등록할 수 있습니다.\n현재 ${count}명 등록되어 있습니다.\n계속 사용하려면 사용신청을 해주세요.`);
        // alert 후 스크롤 잠금 복원
        setTimeout(() => {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        }, 50);
        handleExpiredAction();
    };

    const handleAdd = async () => {
        if (appStatus === 'expired') {
            const { tryExecuteExpiredAction } = await import('../utils/expiredActionLimit');
            const executed = tryExecuteExpiredAction(
                async () => {
                    const trimmed = (note || '').trim();
                    if (!trimmed) return;

                    const historyData = { type, note: trimmed };

                    // Follow Up 정보가 있으면 추가
                    const trimmedFollowUpContent = (followUpContent || '').trim();
                    const trimmedFollowUpDate = (followUpDate || '').trim();

                    if (trimmedFollowUpContent || trimmedFollowUpDate) {
                        historyData.followUp = {
                            content: trimmedFollowUpContent || null,
                            date: trimmedFollowUpDate || null
                        };
                    }
                    const created = await addHistory(historyData);
                    try { if (created) alert('히스토리를 추가했습니다.'); } catch (_) { }
                    setNote('');
                    setFollowUpContent('');
                    setFollowUpDate('');
                },
                handleLimitExceeded
            );
            if (!executed) return;
            return;
        }
        const trimmed = (note || '').trim();
        if (!trimmed) return;

        const historyData = { type, note: trimmed };

        // Follow Up 정보가 있으면 추가
        const trimmedFollowUpContent = (followUpContent || '').trim();
        const trimmedFollowUpDate = (followUpDate || '').trim();

        if (trimmedFollowUpContent || trimmedFollowUpDate) {
            historyData.followUp = {
                content: trimmedFollowUpContent || null,
                date: trimmedFollowUpDate || null
            };
        }
        const created = await addHistory(historyData);
        if (created) {
            sendActionLog('히스토리_추가', {
                customerName: customer?.name || '알 수 없음',
                historyType: type
            });
            try { alert('히스토리를 추가했습니다.'); } catch (_) { }
        }
        setNote('');
        setFollowUpContent('');
        setFollowUpDate('');
    };

    const handleEdit = (entry) => {
        setEditingId(entry.id);
        setEditNote(entry.note);
        setEditType(entry.type);
        setEditFollowUpContent(entry.followUp?.content || '');
        setEditFollowUpDate(entry.followUp?.date || '');
    };

    const handleSaveEdit = async () => {
        if (appStatus === 'expired') {
            const { tryExecuteExpiredAction } = await import('../utils/expiredActionLimit');
            const executed = tryExecuteExpiredAction(
                async () => {
                    const trimmed = (editNote || '').trim();
                    if (!trimmed) return;

                    const historyData = { type: editType, note: trimmed };

                    // Follow Up 정보가 있으면 추가
                    const trimmedFollowUpContent = (editFollowUpContent || '').trim();
                    const trimmedFollowUpDate = (editFollowUpDate || '').trim();

                    if (trimmedFollowUpContent || trimmedFollowUpDate) {
                        historyData.followUp = {
                            content: trimmedFollowUpContent || null,
                            date: trimmedFollowUpDate || null
                        };
                    } else {
                        historyData.followUp = null; // Follow Up 정보가 없으면 null로 설정
                    }

                    const updated = await updateHistory(editingId, historyData);
                    try { if (updated) alert('히스토리를 수정했습니다.'); } catch (_) { }
                    setEditingId(null);
                    setEditNote('');
                    setEditType('note');
                    setEditFollowUpContent('');
                    setEditFollowUpDate('');
                },
                handleLimitExceeded
            );
            if (!executed) return;
            return;
        }
        const trimmed = (editNote || '').trim();
        if (!trimmed) return;

        const historyData = { type: editType, note: trimmed };

        // Follow Up 정보가 있으면 추가
        const trimmedFollowUpContent = (editFollowUpContent || '').trim();
        const trimmedFollowUpDate = (editFollowUpDate || '').trim();

        if (trimmedFollowUpContent || trimmedFollowUpDate) {
            historyData.followUp = {
                content: trimmedFollowUpContent || null,
                date: trimmedFollowUpDate || null
            };
        } else {
            historyData.followUp = null; // Follow Up 정보가 없으면 null로 설정
        }

        const updated = await updateHistory(editingId, historyData);
        if (updated) {
            sendActionLog('히스토리_수정', {
                customerName: customer?.name || '알 수 없음',
                historyType: editType
            });
            try { alert('히스토리를 수정했습니다.'); } catch (_) { }
        }
        setEditingId(null);
        setEditNote('');
        setEditType('note');
        setEditFollowUpContent('');
        setEditFollowUpDate('');
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditNote('');
        setEditType('note');
        setEditFollowUpContent('');
        setEditFollowUpDate('');
    };

    const handleDelete = async (entryId) => {
        if (appStatus === 'expired') {
            const { tryExecuteExpiredAction } = await import('../utils/expiredActionLimit');
            const executed = tryExecuteExpiredAction(
                async () => {
                    try {
                        if (!entryId) return;
                        if (!window.confirm('이 히스토리를 삭제하시겠습니까?')) return;
                        const ok = await deleteHistory(entryId);
                        if (!ok) {
                            console.warn('히스토리 삭제 실패:', entryId);
                        }
                    } catch (e) {
                        console.error('히스토리 삭제 중 오류:', e);
                        alert('삭제 중 오류가 발생했습니다. 다시 시도해주세요.');
                    }
                },
                handleLimitExceeded
            );
            if (!executed) return;
            return;
        }
        try {
            if (!entryId) return;
            if (!window.confirm('이 히스토리를 삭제하시겠습니까?')) return;
            const ok = await deleteHistory(entryId);
            if (ok) {
                sendActionLog('히스토리_삭제', {
                    customerName: customer?.name || '알 수 없음'
                });
            } else {
                console.warn('히스토리 삭제 실패:', entryId);
            }
        } catch (e) {
            console.error('히스토리 삭제 중 오류:', e);
            alert('삭제 중 오류가 발생했습니다. 다시 시도해주세요.');
        }
    };

    const escapeRegExpLocal = (s) => String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const highlightLocal = (text) => {
        const raw = String(text || '');
        const q = (highlightQuery || '').trim();
        if (!q) return raw;
        try {
            const re = new RegExp(escapeRegExpLocal(q), 'ig');
            const parts = raw.split(re);
            const matches = raw.match(re);
            if (!matches) return raw;
            const out = [];
            for (let i = 0; i < parts.length; i++) {
                out.push(parts[i]);
                if (i < parts.length - 1) {
                    out.push(
                        <mark key={`hhl-${i}`} className="bg-yellow-200 text-yellow-900 rounded px-0.5">{matches[i]}</mark>
                    );
                }
            }
            return out;
        } catch (_) { return raw; }
    };

    return (
        <div className="mt-6">
            <h4 className="text-sm font-bold text-gray-800 mb-2">히스토리</h4>
            <div className="space-y-3 mb-3">
                <div className="flex gap-2">
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-2 text-sm bg-white"
                    >
                        <option value="note">메모</option>
                        <option value="call">통화</option>
                        <option value="meet">미팅</option>
                        <option value="todo">할일</option>
                    </select>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="내용 입력... (Enter로 줄바꿈)"
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm resize-y"
                        rows="3"
                    />
                </div>

                {/* Follow Up 입력 필드 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <CalendarCheck className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">Follow Up 계획 (선택사항)</span>
                    </div>
                    <div className="space-y-2">
                        <input
                            type="text"
                            value={followUpContent}
                            onChange={(e) => setFollowUpContent(e.target.value)}
                            placeholder="Follow Up 내용을 입력하세요..."
                            className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm"
                        />
                        <input
                            type="date"
                            value={followUpDate}
                            onChange={(e) => setFollowUpDate(e.target.value)}
                            className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm"
                        />
                    </div>
                </div>

                {/* 추가 버튼 */}
                <button
                    onClick={handleAdd}
                    className="w-full px-3 py-2 bg-gray-800 text-white rounded-lg text-sm font-bold hover:bg-gray-900 transition-colors"
                    title={appStatus === 'expired' ? '누적 10명 제한' : ''}
                >
                    히스토리 추가
                </button>
            </div>
            <div className="max-h-60 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-xl bg-white/70">
                {history.length === 0 && (
                    <div className="text-sm text-gray-500 p-3">기록이 없습니다</div>
                )}
                {history.map((h) => (
                    <div key={h.id} className="px-3 py-2 text-sm">
                        {editingId === h.id ? (
                            // 수정 모드
                            <div className="space-y-3">
                                <div className="flex gap-2">
                                    <select
                                        value={editType}
                                        onChange={(e) => setEditType(e.target.value)}
                                        className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white"
                                    >
                                        <option value="note">메모</option>
                                        <option value="call">통화</option>
                                        <option value="meet">미팅</option>
                                        <option value="todo">할일</option>
                                    </select>
                                    <textarea
                                        value={editNote}
                                        onChange={(e) => setEditNote(e.target.value)}
                                        className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-xs resize-y"
                                        rows="3"
                                    />
                                </div>

                                {/* Follow Up 수정 필드 */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                                    <div className="flex items-center gap-1 mb-2">
                                        <CalendarCheck className="w-3 h-3 text-blue-600" />
                                        <span className="text-xs font-medium text-blue-800">Follow Up 계획</span>
                                    </div>
                                    <div className="space-y-1">
                                        <input
                                            type="text"
                                            value={editFollowUpContent}
                                            onChange={(e) => setEditFollowUpContent(e.target.value)}
                                            placeholder="Follow Up 내용..."
                                            className="w-full border border-blue-200 rounded px-2 py-1 text-xs"
                                        />
                                        <input
                                            type="date"
                                            value={editFollowUpDate}
                                            onChange={(e) => setEditFollowUpDate(e.target.value)}
                                            className="w-full border border-blue-200 rounded px-2 py-1 text-xs"
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSaveEdit}
                                        className="px-2 py-1 bg-green-500 text-white rounded text-xs font-bold hover:bg-green-600"
                                        title={appStatus === 'expired' ? '누적 10명 제한' : ''}
                                    >
                                        저장
                                    </button>
                                    <button
                                        onClick={handleCancelEdit}
                                        className="px-2 py-1 bg-gray-500 text-white rounded text-xs font-bold hover:bg-gray-600"
                                    >
                                        취소
                                    </button>
                                </div>
                            </div>
                        ) : (
                            // 일반 보기 모드
                            <div className="flex items-start gap-2">
                                <span className="inline-flex px-2 py-1 rounded-md text-xs font-bold bg-gray-100 text-gray-700">
                                    {h.type}
                                </span>
                                <div className="flex-1">
                                    <div className="text-gray-800 whitespace-pre-line">{highlightLocal(h.note)}</div>

                                    {/* Follow Up 계획 표시 */}
                                    {h.followUp && (
                                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                            <div className="flex items-start gap-2">
                                                <CalendarCheck className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1">
                                                    <div className="text-xs font-medium text-blue-800 mb-1">
                                                        Follow Up 계획
                                                    </div>
                                                    {h.followUp.content && (
                                                        <div className="text-xs text-blue-700 mb-1">
                                                            {highlightLocal(h.followUp.content)}
                                                        </div>
                                                    )}
                                                    {h.followUp.date && (
                                                        <div className="text-xs text-blue-600">
                                                            예정일: {new Date(h.followUp.date).toLocaleDateString('ko-KR', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="text-xs text-gray-400">
                                        {formatKoreanTimeUserFriendly(h.at)}
                                        {h.updatedAt && (
                                            <span className="ml-2 text-blue-500">(수정됨)</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => handleEdit(h)}
                                        className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                        title="수정"
                                    >
                                        <Edit className="w-3 h-3" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(h.id)}
                                        className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                        title="삭제"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const CustomerOverview = ({
    customers,
    onShowAddForm,
    onEditCustomer,
    onDeleteCustomer,
    onMoveCustomer,
    onLoadMoreStage,
    setCurrentStep,
    businessType = 'insurance',
    appStatus,
    trialInfo,
    showListView = false,
    onOpenArcade,
    onBulkMove
}) => {
    const navigate = useNavigate();
    const { sendActionLog } = useActionLog();
    // 각 단계별 ref 생성
    const stageRefs = useRef({});
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [openingCustomerId, setOpeningCustomerId] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showAudiobookModal, setShowAudiobookModal] = useState(false);
    const [showFavoriteModal, setShowFavoriteModal] = useState(false);
    const [listViewSortBy, setListViewSortBy] = useState('name'); // 'name' 또는 'createdAt'
    const [listViewSortOrder, setListViewSortOrder] = useState('desc'); // 'asc' 또는 'desc'

    // Selection Mode
    const [selectionModeStageId, setSelectionModeStageId] = useState(null);
    const [selectedCustomerIds, setSelectedCustomerIds] = useState(new Set());

    const toggleSelectionMode = (stageId) => {
        if (selectionModeStageId === stageId) {
            setSelectionModeStageId(null);
            setSelectedCustomerIds(new Set());
        } else {
            setSelectionModeStageId(stageId);
            setSelectedCustomerIds(new Set());
        }
    };

    const toggleCustomerSelection = (customerId) => {
        setSelectedCustomerIds(prev => {
            const next = new Set(prev);
            if (next.has(customerId)) {
                next.delete(customerId);
            } else {
                next.add(customerId);
            }
            return next;
        });
    };

    const handleSelectAll = (stageId, stageCustomers) => {
        if (selectedCustomerIds.size === stageCustomers.length) {
            setSelectedCustomerIds(new Set());
        } else {
            const allIds = new Set(stageCustomers.map(c => c.id));
            setSelectedCustomerIds(allIds);
        }
    };

    const handleBulkMoveClick = () => {
        if (selectedCustomerIds.size === 0) return;
        const stageCustomers = customers[selectionModeStageId] || [];
        const customersToMove = stageCustomers.filter(c => selectedCustomerIds.has(c.id));
        if (onBulkMove) onBulkMove(customersToMove);
        setSelectionModeStageId(null);
        setSelectedCustomerIds(new Set());
    };

    // ref 초기화
    const CUSTOMER_STAGES = useMemo(() => getCustomerStages(businessType), [businessType]);

    // 만료 상태일 때 업그레이드 유도
    const handleExpiredAction = () => {
        try {
            window.dispatchEvent(new Event('openApprovalModal'));
        } catch (e) {
            console.warn('[CustomerOverview] dispatch failed', e);
            alert('체험 기간이 만료되었습니다. 계속 사용하려면 사용신청을 해주세요.');
        }
    };

    // 만료 상태일 때 제한 초과 알림
    const handleLimitExceeded = async () => {
        const { getExpiredCustomerCount } = await import('../utils/expiredActionLimit');
        const count = await getExpiredCustomerCount();
        alert(`체험 기간이 만료되어 누적 10명까지만 등록할 수 있습니다.\n현재 ${count}명 등록되어 있습니다.\n계속 사용하려면 사용신청을 해주세요.`);
        // alert 후 스크롤 잠금 복원
        setTimeout(() => {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        }, 50);
        handleExpiredAction();
    };

    // 만료 상태일 때 추가 폼 열기 (누적 고객 등록수 제한)
    const handleShowAddForm = async (stageId) => {
        if (appStatus === 'expired') {
            const { tryAddExpiredCustomer } = await import('../utils/expiredActionLimit');
            const executed = await tryAddExpiredCustomer(
                () => onShowAddForm(stageId),
                handleLimitExceeded
            );
            if (!executed) return;
            return;
        }
        onShowAddForm(stageId);
    };

    // 만료 상태일 때 수정 (제한 없음)
    const handleEditCustomer = async (customer) => {
        onEditCustomer(customer);
    };

    // 만료 상태일 때 삭제 (제한 없음)
    const handleDeleteCustomer = async (customer) => {
        onDeleteCustomer(customer);
    };

    // 정렬 상태 관리 (각 단계별로 개별 관리, 기본값은 true)
    const [sortByName, setSortByName] = useState({});

    // 모달 열릴 때 전체 페이지 스크롤 비활성화
    useEffect(() => {
        if (selectedCustomer) {
            // 현재 스크롤 위치 저장
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        } else {
            // 스크롤 복원
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }

        // cleanup 함수
        return () => {
            if (!selectedCustomer) {
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
            }
        };
    }, [selectedCustomer]);

    // ESC 키로 모달 닫기
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && selectedCustomer) {
                setSelectedCustomer(null);
            }
        };

        if (selectedCustomer) {
            window.addEventListener('keydown', handleEscKey);
        }

        return () => {
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [selectedCustomer]);

    // 단계별 보이는 고객 수 제한(초기 50명, 더 보기로 +50씩)
    const INITIAL_VISIBLE = 50;
    const LOAD_MORE_STEP = 50;
    const [visibleCounts, setVisibleCounts] = useState({});
    useEffect(() => {
        try {
            const init = {};
            (CUSTOMER_STAGES || []).forEach(s => { init[s.id] = INITIAL_VISIBLE; });
            setVisibleCounts(init);
        } catch (_) { }
    }, [CUSTOMER_STAGES]);

    const handleLoadMore = async (stageId) => {
        setVisibleCounts(prev => ({
            ...prev,
            [stageId]: (prev?.[stageId] || INITIAL_VISIBLE) + LOAD_MORE_STEP
        }));
        // 부모로부터 IndexedDB 추가 로딩 요청 (있다면)
        if (typeof onLoadMoreStage === 'function') {
            try { await onLoadMoreStage(stageId); } catch (_) { }
        }
    };

    // 정렬 토글 함수
    const toggleSortByName = (stageId) => {
        setSortByName(prev => ({
            ...prev,
            [stageId]: !prev[stageId]
        }));
    };

    const handleCustomerClick = (customer) => {
        // 즉시 터치 반응: 로딩 오버레이 표시 후 다음 틱에 상세 모달 오픈
        setOpeningCustomerId(customer.id);
        setTimeout(() => {
            try {
                setSelectedCustomer(customer);
                // 고객 상세보기 로깅
                sendActionLog('고객_상세보기', {
                    customerName: customer.name
                });
            } finally {
                setOpeningCustomerId(null);
            }
        }, 0);
    };

    // 데일리 트리거/주간 목표 상태
    const [missionText, setMissionText] = useState('오늘부터 FC퍼널로 체계적인 영업을 시작한다!');
    const [showMissionEditor, setShowMissionEditor] = useState(false);
    const [tempMission, setTempMission] = useState('');

    // 배너 캐러셀 상태
    const [activeBanner, setActiveBanner] = useState(0); // 0: 오늘의 미션, 1: 이번달 목표, 2: 올해의 목표
    const [touchStartX, setTouchStartX] = useState(null);
    const [touchDeltaX, setTouchDeltaX] = useState(0);

    // 오디오북 관련 상태
    const audioRef = useRef(null);
    const [audiobook, setAudiobook] = useState(null);
    const [, setIsPlaying] = useState(false);
    const [playCounted, setPlayCounted] = useState(false);
    const [hasNewAudiobook, setHasNewAudiobook] = useState(false);

    // 목표 텍스트 (월간/연간)
    const [monthlyGoalText, setMonthlyGoalText] = useState('이번 달 목표를 설정하세요');
    const [yearlyGoalText, setYearlyGoalText] = useState('올해의 목표를 설정하세요');
    const [showMonthlyEditor, setShowMonthlyEditor] = useState(false);
    const [showYearlyEditor, setShowYearlyEditor] = useState(false);
    const [tempMonthly, setTempMonthly] = useState('');
    const [tempYearly, setTempYearly] = useState('');

    // '사용방법' 버튼 클릭 횟수 추적
    const [usageGuideClickCount, setUsageGuideClickCount] = useState(0);

    // localStorage에서 클릭 횟수 로드
    useEffect(() => {
        try {
            const savedCount = localStorage.getItem('usageGuideClickCount');
            if (savedCount !== null) {
                setUsageGuideClickCount(parseInt(savedCount, 10));
            }
        } catch (error) {
            console.warn('사용방법 버튼 클릭 횟수 로드 실패:', error);
        }
    }, []);

    useEffect(() => {
        // AI 비서/히스토리 등에서 고객 선택 이벤트 수신 → 오버레이 오픈
        const handler = (e) => {
            const customer = e.detail?.customer;
            if (!customer) return;

            // customers에서 최신 고객 정보 찾기 (단계 이동 후 최신 상태 반영)
            let latestCustomer = null;
            let stageId = null;

            for (const [stage, stageCustomers] of Object.entries(customers)) {
                const found = stageCustomers.find(c => c.id === customer.id);
                if (found) {
                    latestCustomer = found;
                    stageId = stage;
                    break;
                }
            }

            // 최신 정보가 있으면 사용, 없으면 전달받은 customer 사용
            if (latestCustomer) {
                setSelectedCustomer({ ...latestCustomer, __stageId: stageId });
            } else {
                setSelectedCustomer(customer);
            }
        };
        window.addEventListener('openCustomerOverlayFromAssistant', handler);
        return () => window.removeEventListener('openCustomerOverlayFromAssistant', handler);
    }, [customers]);

    useEffect(() => {
        // 단계 이동 모달이 닫힐 때 고객 상세 모달도 닫기
        const handler = () => {
            setSelectedCustomer(null);
        };
        window.addEventListener('closeCustomerOverlay', handler);
        return () => window.removeEventListener('closeCustomerOverlay', handler);
    }, []);

    useEffect(() => {
        // 초기 로드: KST 기준 키로 일/주 진행상태 불러오기
        const loadMissionData = async () => {
            try {
                await openDb();

                // IndexedDB에서 로드 시도
                let missionText = await idbGetSetting('dailyMissionText');
                let monthlyGoalText = await idbGetSetting('monthlyGoalText');
                let yearlyGoalText = await idbGetSetting('yearlyGoalText');

                // IndexedDB에 없으면 localStorage에서 마이그레이션
                if (!missionText) {
                    const missionRaw = localStorage.getItem('dailyMissionText');
                    if (missionRaw && typeof missionRaw === 'string') {
                        missionText = missionRaw;
                        await idbSetSetting('dailyMissionText', missionText);
                        localStorage.removeItem('dailyMissionText'); // 마이그레이션 후 제거
                    }
                }
                if (!monthlyGoalText) {
                    const monthlyRaw = localStorage.getItem('monthlyGoalText');
                    if (monthlyRaw && typeof monthlyRaw === 'string') {
                        monthlyGoalText = monthlyRaw;
                        await idbSetSetting('monthlyGoalText', monthlyGoalText);
                        localStorage.removeItem('monthlyGoalText'); // 마이그레이션 후 제거
                    }
                }
                if (!yearlyGoalText) {
                    const yearlyRaw = localStorage.getItem('yearlyGoalText');
                    if (yearlyRaw && typeof yearlyRaw === 'string') {
                        yearlyGoalText = yearlyRaw;
                        await idbSetSetting('yearlyGoalText', yearlyGoalText);
                        localStorage.removeItem('yearlyGoalText'); // 마이그레이션 후 제거
                    }
                }

                if (missionText && typeof missionText === 'string') setMissionText(missionText);
                if (monthlyGoalText && typeof monthlyGoalText === 'string') setMonthlyGoalText(monthlyGoalText);
                if (yearlyGoalText && typeof yearlyGoalText === 'string') setYearlyGoalText(yearlyGoalText);

            } catch (e) {
                console.error('미션 데이터 로드 실패:', e);
                // 실패 시 localStorage 폴백
                try {
                    const missionRaw = localStorage.getItem('dailyMissionText');
                    const monthlyRaw = localStorage.getItem('monthlyGoalText');
                    const yearlyRaw = localStorage.getItem('yearlyGoalText');

                    if (missionRaw && typeof missionRaw === 'string') setMissionText(missionRaw);
                    if (monthlyRaw && typeof monthlyRaw === 'string') setMonthlyGoalText(monthlyRaw);
                    if (yearlyRaw && typeof yearlyRaw === 'string') setYearlyGoalText(yearlyRaw);
                } catch (_) { /* noop */ }
            }
        };
        loadMissionData();
    }, []);

    const openMissionEditor = () => {
        setTempMission(missionText);
        setShowMissionEditor(true);
    };

    const saveMission = async () => {
        const text = (tempMission || '').trim();
        if (!text) return;
        setMissionText(text);
        try {
            await openDb();
            await idbSetSetting('dailyMissionText', text);
        } catch (e) {
            console.error('미션 저장 실패:', e);
            // 폴백: localStorage에 저장
            localStorage.setItem('dailyMissionText', text);
        }
        setShowMissionEditor(false);
    };

    const saveMonthlyGoal = async () => {
        const text = (tempMonthly || '').trim();
        if (!text) return;
        setMonthlyGoalText(text);
        try {
            await openDb();
            await idbSetSetting('monthlyGoalText', text);
        } catch (e) {
            console.error('월간 목표 저장 실패:', e);
            // 폴백: localStorage에 저장
            localStorage.setItem('monthlyGoalText', text);
        }
        setShowMonthlyEditor(false);
    };

    const saveYearlyGoal = async () => {
        const text = (tempYearly || '').trim();
        if (!text) return;
        setYearlyGoalText(text);
        try {
            await openDb();
            await idbSetSetting('yearlyGoalText', text);
        } catch (e) {
            console.error('연간 목표 저장 실패:', e);
            // 폴백: localStorage에 저장
            localStorage.setItem('yearlyGoalText', text);
        }
        setShowYearlyEditor(false);
    };

    // 캐러셀 스와이프 핸들러
    const onTouchStart = (e) => {
        setTouchStartX(e.touches[0].clientX);
        setTouchDeltaX(0);
    };
    const onTouchMove = (e) => {
        if (touchStartX == null) return;
        const dx = e.touches[0].clientX - touchStartX;
        setTouchDeltaX(dx);
    };
    const onTouchEnd = () => {
        if (Math.abs(touchDeltaX) > 60) {
            if (touchDeltaX < 0) setActiveBanner(prev => Math.min(2, prev + 1));
            else setActiveBanner(prev => Math.max(0, prev - 1));
        }
        setTouchStartX(null);
        setTouchDeltaX(0);
    };

    // (원형 타이머 제거)

    // 오디오북 로드
    useEffect(() => {
        const loadAudiobook = async () => {
            try {
                const audiobookData = await getActiveAudiobook();
                if (audiobookData && audiobookData.downloadURL) {
                    setAudiobook(audiobookData);

                    // 새로운 컨텐츠 확인
                    const lastCheckedAudiobookId = localStorage.getItem('lastCheckedAudiobookId');
                    if (lastCheckedAudiobookId !== audiobookData.id) {
                        setHasNewAudiobook(true);
                    } else {
                        setHasNewAudiobook(false);
                    }
                } else {
                    setAudiobook(null);
                    setHasNewAudiobook(false);
                }
            } catch (e) {
                console.error('오디오북 로드 실패:', e);
                setAudiobook(null);
                setHasNewAudiobook(false);
            }
        };
        loadAudiobook();
    }, []);

    // 오디오북 모달이 열릴 때 배지 제거
    useEffect(() => {
        if (showAudiobookModal && audiobook?.id && hasNewAudiobook) {
            localStorage.setItem('lastCheckedAudiobookId', audiobook.id);
            setHasNewAudiobook(false);
        }
    }, [showAudiobookModal, audiobook?.id, hasNewAudiobook]);

    // 오디오 엘리먼트 초기화 및 이벤트 리스너
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !audiobook) return;

        const handlePlay = async () => {
            setIsPlaying(true);
            if (!playCounted) {
                try {
                    await incrementAudiobookPlay();
                } catch (e) {
                    console.warn('재생 카운트 실패', e);
                }
                setPlayCounted(true);
            }
        };

        const handlePause = () => {
            setIsPlaying(false);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setPlayCounted(false);
        };

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [audiobook, playCounted]);

    useEffect(() => {
        CUSTOMER_STAGES.forEach(stage => {
            if (!stageRefs.current[stage.id]) {
                stageRefs.current[stage.id] = React.createRef();
            }
        });
    }, [CUSTOMER_STAGES]);

    // 오디오 소스 업데이트
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (audiobook && audiobook.downloadURL) {
            audio.src = audiobook.downloadURL;
            audio.load();
        } else {
            audio.src = '';
        }
    }, [audiobook]);

    // 스크롤 이동 함수 - ref 사용
    const scrollToStage = (stageId) => {
        const element = stageRefs.current[stageId]?.current;

        if (element) {
            // 상단 헤더 높이를 고려한 오프셋
            const headerHeight = 120;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset - headerHeight;

            window.scrollTo({
                top: elementPosition,
                behavior: 'smooth'
            });

            // 강조 효과
            element.style.transition = 'all 0.3s ease';
            element.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.5)';
            element.style.transform = 'scale(1.02)';

            setTimeout(() => {
                element.style.boxShadow = '';
                element.style.transform = '';
            }, 2000);

        } else {

        }
    };

    // 전화걸기 기능
    const handleCall = (phoneNumber) => {
        if (phoneNumber) {
            window.location.href = `tel:${phoneNumber}`;
        }
    };

    // 카톡 보내기 모달 상태 및 메시지 전송
    const [showKakaoModal, setShowKakaoModal] = useState(false);
    const [kakaoMessage, setKakaoMessage] = useState('');

    const isMobileDevice = () => {
        try {
            const ua = navigator.userAgent || navigator.vendor || window.opera;
            return /Android|iPhone|iPad|iPod/i.test(ua);
        } catch (_) {
            return false;
        }
    };

    const canUseWebShare = () => {
        try {
            return !!navigator.share;
        } catch (_) {
            return false;
        }
    };

    const handleSendKakao = async () => {
        const message = (kakaoMessage || '').trim();
        if (!message) {
            alert('메시지를 입력하세요');
            return;
        }

        // 우선 Web Share API 사용: 카카오톡 포함 OS 공유 시트
        if (canUseWebShare()) {
            try {
                await navigator.share({
                    text: message
                });
                setShowKakaoModal(false);
                setKakaoMessage('');
                return;
            } catch (e) {
                // 사용자가 취소하거나 미지원
            }
        }

        // 폴백: 메시지 복사 후 안내
        try {
            await navigator.clipboard.writeText(message);
            alert('메시지를 복사했어요. 카카오톡에서 붙여넣기 해주세요.');
        } catch (_) {
            const ta = document.createElement('textarea');
            ta.value = message;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            alert('메시지를 복사했어요. 카카오톡에서 붙여넣기 해주세요.');
        }

        setShowKakaoModal(false);
        setKakaoMessage('');
    };

    // 히스토리 모달에서 단계로 이동하는 함수
    const handleNavigateToStage = (customerId, stageName) => {
        // 고객이 있는 단계 찾기
        for (const [stageId, stageCustomers] of Object.entries(customers)) {
            if (stageCustomers.some(c => c.id === customerId)) {
                // 해당 단계로 스크롤 이동
                scrollToStage(stageId);
                break;
            }
        }
    };

    // 히스토리 모달에서 고객 카드를 선택했을 때 상세 모달 열기
    const handleSelectCustomerFromHistory = (customerId) => {
        for (const [stageId, stageCustomers] of Object.entries(customers)) {
            const customer = (stageCustomers || []).find(c => c.id === customerId);
            if (customer) {
                setSelectedCustomer({ ...customer, __stageId: stageId });
                break;
            }
        }
    };

    // 고객 카드 클릭 핸들러 (상단에서 rAF 스로틀 + 로딩 오버레이 적용된 공용 핸들러 사용)

    // 오버레이 닫기
    const closeOverlay = () => {
        setSelectedCustomer(null);
    };

    // 검색 상태
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const searchScope = 'all'; // all | customer | memo | history
    const [historiesByCustomer, setHistoriesByCustomer] = useState({});
    const [showScrollBanner, setShowScrollBanner] = useState(() => {
        // localStorage에서 배너 표시 여부 확인
        try {
            return localStorage.getItem('scrollBannerDismissed') !== 'true';
        } catch (_) {
            return true;
        }
    });

    const escapeRegExp = (s) => String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const highlightText = (text, query) => {
        const raw = String(text || '');
        const q = (query || '').trim();
        if (!q) return raw;
        try {
            const re = new RegExp(escapeRegExp(q), 'ig');
            const parts = raw.split(re);
            const matches = raw.match(re);
            if (!matches) return raw;
            const out = [];
            for (let i = 0; i < parts.length; i++) {
                out.push(parts[i]);
                if (i < parts.length - 1) {
                    out.push(
                        <mark key={`hl-${i}`} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
                            {matches[i]}
                        </mark>
                    );
                }
            }
            return out;
        } catch (_) {
            return raw;
        }
    };

    // 모든 고객 히스토리를 한 번에 로드하여 검색에 사용 (비동기 → 상태에 보관)
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { idbGetAllHistories } = await import('../utils/idb');
                const all = await idbGetAllHistories();
                if (cancelled) return;
                const map = {};
                (all || []).forEach(h => {
                    const cid = h.customerId;
                    if (!map[cid]) map[cid] = [];
                    map[cid].push(h);
                });
                setHistoriesByCustomer(map);
            } catch (_) { /* ignore */ }
        };
        load();
        return () => { cancelled = true; };
    }, [customers]);
    const normalize = (str) => (str || '').toString().trim().toLowerCase();
    const allCustomers = Object.entries(customers).flatMap(([stageId, list]) =>
        (list || []).map(c => ({ ...c, __stageId: stageId }))
    );

    // 검색 실행 함수
    const performSearch = async () => {
        const q = normalize(searchQuery);
        if (!q) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            // 페이지네이션과 관계없이 IndexedDB에서 직접 모든 고객 조회
            const { idbGetAllCustomers } = await import('../utils/idb');
            const allCustomersFromIdb = await idbGetAllCustomers();

            const results = allCustomersFromIdb.filter(c => {
                const inCustomer = [c.name, c.phone, c.company, c.address, c.detailAddress, c.relation]
                    .some(v => normalize(v).includes(q));
                const inMemo = [c.memo, c.keyword].some(v => normalize(v).includes(q));
                let inHistory = false;
                const hlist = historiesByCustomer[c.id] || [];
                for (const h of hlist) {
                    const text = [h?.note, h?.type, h?.followUp?.content]
                        .map(v => normalize(v))
                        .join(' ');
                    if (text.includes(q)) { inHistory = true; break; }
                }

                if (searchScope === 'customer') return inCustomer;
                if (searchScope === 'memo') return inMemo;
                if (searchScope === 'history') return inHistory;
                // all
                return inCustomer || inMemo || inHistory;
            });

            setSearchResults(results);
        } catch (error) {
            console.error('검색 실패:', error);
            // 폴백: 메모리의 customers 사용
            const results = allCustomers.filter(c => {
                const inCustomer = [c.name, c.phone, c.company, c.address, c.detailAddress, c.relation]
                    .some(v => normalize(v).includes(q));
                const inMemo = [c.memo, c.keyword].some(v => normalize(v).includes(q));
                let inHistory = false;
                const hlist = historiesByCustomer[c.id] || [];
                for (const h of hlist) {
                    const text = [h?.note, h?.type, h?.followUp?.content]
                        .map(v => normalize(v))
                        .join(' ');
                    if (text.includes(q)) { inHistory = true; break; }
                }

                if (searchScope === 'customer') return inCustomer;
                if (searchScope === 'memo') return inMemo;
                if (searchScope === 'history') return inHistory;
                // all
                return inCustomer || inMemo || inHistory;
            });
            setSearchResults(results);
        } finally {
            setIsSearching(false);
        }
    };

    // 검색어가 변경되면 자동으로 검색 결과 초기화
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
        }
    }, [searchQuery]);

    return (
        <div className="min-h-screen p-6">
            <div className="max-w-7xl mx-auto">
                {/* 거부 상태 안내 배너 */}
                {trialInfo && trialInfo.isRejected && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                        <p className="text-sm font-semibold text-yellow-800">
                            {appStatus === 'expired' ? (
                                <>
                                    ⚠️ 승인 요청이 거부되었습니다. 체험기간이 종료되어 누적 10명까지만 등록할 수 있습니다.
                                </>
                            ) : (
                                <>
                                    ⚠️ 승인 요청이 거부되었지만, 체험기간이 남아있어 계속 사용하실 수 있습니다.
                                </>
                            )}
                        </p>
                    </div>
                )}
                {/* 체험 안내 문구 */}
                {(appStatus === 'trial' || (appStatus === 'pending' && trialInfo && trialInfo.status === 'trial')) && (
                    (() => {
                        try {
                            // trialInfo에서 daysRemaining을 우선 사용, 없으면 localStorage의 trialStartDate 사용
                            let daysLeft = null;

                            if (trialInfo && typeof trialInfo.daysRemaining === 'number') {
                                daysLeft = trialInfo.daysRemaining;
                            } else {
                                // 공통 유틸리티 함수 사용
                                const trialStartDate = localStorage.getItem('trialStartDate');
                                if (trialStartDate) {
                                    daysLeft = calculateRemainingDays(trialStartDate);
                                } else {
                                    // 폴백: verificationDate 사용 (기존 호환성)
                                    const v = localStorage.getItem('verificationDate');
                                    if (v) {
                                        daysLeft = calculateRemainingDays(v);
                                    }
                                }
                            }

                            if (daysLeft === null) return null;

                            return (
                                <div className="mb-2 text-center text-sm font-semibold text-red-600">
                                    체험기간이 {daysLeft}일 남았습니다
                                </div>
                            );
                        } catch (_) {
                            return null;
                        }
                    })()
                )}

                {/* 기능 버튼들 */}
                <div className="mb-4 flex flex-wrap gap-2.5">
                    {/* 사용신청 버튼 (체험중 또는 체험만료 상태일 때만 표시) */}
                    {(appStatus === 'trial' || appStatus === 'expired') && (
                        <motion.button
                            onClick={() => {
                                // GA4: 사용신청 버튼 클릭 이벤트 추적
                                try {
                                    const gtag = window.gtag;
                                    const measurementId = window.GA_MEASUREMENT_ID;
                                    if (typeof gtag === 'function' && measurementId) {
                                        const deviceId = generateDeviceId();
                                        gtag('event', 'application_button_click', {
                                            app_status: appStatus,
                                            button_location: 'customer_overview',
                                            event_category: 'application',
                                            event_label: '사용신청 버튼 클릭',
                                            device_id: deviceId
                                        });
                                    }
                                } catch (gaError) {
                                    console.warn('GA4 이벤트 전송 실패:', gaError);
                                }

                                try {
                                    window.dispatchEvent(new Event('openApprovalModal'));
                                } catch (e) {
                                    console.warn('[CustomerOverview] dispatch failed', e);
                                    alert('사용신청 모달을 열 수 없습니다.');
                                }
                            }}
                            className="group relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-medium text-xs bg-red-500 text-white border border-red-600 hover:bg-red-600 hover:shadow-lg hover:shadow-red-200/50 shadow-sm shadow-red-100/50 backdrop-blur-sm transition-all duration-300"
                            title="FC퍼널 사용신청하기"
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                        >
                            <div className="p-1.5 rounded-lg bg-red-600/60 group-hover:bg-red-600/80 group-hover:scale-110 transition-all duration-300">
                                <ClipboardCheck className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className="tracking-tight hidden md:inline">사용신청</span>
                        </motion.button>
                    )}

                    {/* 오디오북 재생 버튼 */}
                    <motion.button
                        onClick={() => {
                            if (audiobook) {
                                // GA4: 오디오북 버튼 클릭 이벤트 추적
                                try {
                                    const gtag = window.gtag;
                                    const measurementId = window.GA_MEASUREMENT_ID;
                                    if (typeof gtag === 'function' && measurementId) {
                                        const deviceId = generateDeviceId();
                                        gtag('event', 'audiobook_button_click', {
                                            button_location: 'main_navigation',
                                            event_category: 'navigation',
                                            event_label: '오디오북 버튼 클릭',
                                            device_id: deviceId
                                        });
                                    }
                                } catch (gaError) {
                                    console.warn('GA4 이벤트 전송 실패:', gaError);
                                }

                                // 새로운 컨텐츠 확인 처리
                                if (hasNewAudiobook && audiobook.id) {
                                    localStorage.setItem('lastCheckedAudiobookId', audiobook.id);
                                    setHasNewAudiobook(false);
                                }
                                setShowAudiobookModal(true);
                            } else {
                                alert('재생 가능한 오디오북이 없습니다.');
                            }
                        }}
                        className={`group relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-medium text-xs transition-all duration-300 border backdrop-blur-sm ${audiobook
                            ? 'bg-gradient-to-br from-gray-50 via-white to-gray-50/80 text-gray-800 border-gray-300/60 hover:border-gray-400/80 hover:shadow-lg hover:shadow-gray-200/50 shadow-sm shadow-gray-100/50'
                            : 'bg-gray-100/50 text-gray-400 border-gray-200/50 cursor-not-allowed'
                            }`}
                        disabled={!audiobook}
                        title={audiobook ? '오디오북 재생 컨트롤러 열기' : '재생 가능한 오디오북이 없습니다'}
                        variants={buttonVariants}
                        whileHover={audiobook ? "hover" : {}}
                        whileTap={audiobook ? "tap" : {}}
                    >
                        <div className={`p-1.5 rounded-lg transition-all duration-300 ${audiobook ? 'bg-gray-200/60 group-hover:bg-gray-200/80 group-hover:scale-110' : 'bg-gray-200/30'}`}>
                            <Headphones className="w-3.5 h-3.5 text-gray-700" />
                        </div>
                        <span className="tracking-tight hidden md:inline">{audiobook ? '오디오북' : '오디오북 없음'}</span>
                        {hasNewAudiobook && audiobook && (
                            <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full border-2 border-white animate-pulse shadow-sm" title="새로운 컨텐츠가 있습니다">
                                NEW
                            </span>
                        )}
                    </motion.button>

                    {/* 기능 버튼 1 - YouTube 링크 */}
                    <motion.button
                        onClick={() => {
                            // 클릭 횟수 증가 및 저장
                            const newCount = usageGuideClickCount + 1;
                            setUsageGuideClickCount(newCount);
                            try {
                                localStorage.setItem('usageGuideClickCount', newCount.toString());
                            } catch (error) {
                                console.warn('사용방법 버튼 클릭 횟수 저장 실패:', error);
                            }

                            // GA4: 영상 보기 버튼 클릭 이벤트 추적
                            try {
                                const gtag = window.gtag;
                                const measurementId = window.GA_MEASUREMENT_ID;
                                if (typeof gtag === 'function' && measurementId) {
                                    const deviceId = generateDeviceId();
                                    gtag('event', 'video_button_click', {
                                        button_location: 'main_navigation',
                                        event_category: 'navigation',
                                        event_label: '영상 보기 버튼 클릭',
                                        device_id: deviceId
                                    });
                                }
                            } catch (gaError) {
                                console.warn('GA4 이벤트 전송 실패:', gaError);
                            }

                            window.open('https://youtu.be/4M1woDa1Cbc', '_blank', 'noopener,noreferrer');
                        }}
                        className="group relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-medium text-xs bg-gradient-to-br from-gray-50 via-white to-gray-50/80 text-gray-800 border border-gray-300/60 hover:border-gray-400/80 hover:shadow-lg hover:shadow-gray-200/50 shadow-sm shadow-gray-100/50 backdrop-blur-sm transition-all duration-300"
                        title="사용방법"
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                    >
                        {/* 강조 배지 - 3번 미만 클릭 시에만 표시 */}
                        {usageGuideClickCount < 3 && (
                            <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 bg-gradient-to-br from-red-500 to-red-600 text-white text-[10px] font-bold rounded-full shadow-lg shadow-red-500/50 animate-pulse">
                                !
                            </span>
                        )}
                        <div className="p-1.5 rounded-lg bg-gray-200/60 group-hover:bg-gray-200/80 group-hover:scale-110 transition-all duration-300">
                            <Video className="w-3.5 h-3.5 text-gray-700" />
                        </div>
                        <span className="tracking-tight hidden md:inline">사용방법</span>
                    </motion.button>

                    {/* 업무일지 버튼 */}
                    <motion.button
                        onClick={() => {
                            sendActionLog('업무일지_열기');

                            // GA4: 업무일지 버튼 클릭 이벤트 추적
                            try {
                                const gtag = window.gtag;
                                const measurementId = window.GA_MEASUREMENT_ID;
                                if (typeof gtag === 'function' && measurementId) {
                                    const deviceId = generateDeviceId();
                                    gtag('event', 'work_diary_button_click', {
                                        button_location: 'main_navigation',
                                        event_category: 'navigation',
                                        event_label: '업무일지 버튼 클릭',
                                        device_id: deviceId
                                    });
                                }
                            } catch (gaError) {
                                console.warn('GA4 이벤트 전송 실패:', gaError);
                            }

                            navigate('/work-diary');
                        }}
                        className="group relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-medium text-xs bg-gradient-to-br from-gray-50 via-white to-gray-50/80 text-gray-800 border border-gray-300/60 hover:border-gray-400/80 hover:shadow-lg hover:shadow-gray-200/50 shadow-sm shadow-gray-100/50 backdrop-blur-sm transition-all duration-300"
                        title="업무일지"
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                    >
                        <div className="p-1.5 rounded-lg bg-gray-200/60 group-hover:bg-gray-200/80 group-hover:scale-110 transition-all duration-300">
                            <BookOpen className="w-3.5 h-3.5 text-gray-700" />
                        </div>
                        <span className="tracking-tight hidden md:inline">업무일지</span>
                    </motion.button>

                    {/* 소개트리 버튼 */}
                    <motion.button
                        onClick={() => {
                            sendActionLog('소개트리_열기');

                            // GA4: 소개트리 버튼 클릭 이벤트 추적
                            try {
                                const gtag = window.gtag;
                                const measurementId = window.GA_MEASUREMENT_ID;
                                if (typeof gtag === 'function' && measurementId) {
                                    const deviceId = generateDeviceId();
                                    gtag('event', 'referral_tree_button_click', {
                                        button_location: 'main_navigation',
                                        event_category: 'navigation',
                                        event_label: '소개트리 버튼 클릭',
                                        device_id: deviceId
                                    });
                                }
                            } catch (gaError) {
                                console.warn('GA4 이벤트 전송 실패:', gaError);
                            }

                            navigate('/customer-referral-tree');
                        }}
                        className="group relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-medium text-xs bg-gradient-to-br from-gray-50 via-white to-gray-50/80 text-gray-800 border border-gray-300/60 hover:border-gray-400/80 hover:shadow-lg hover:shadow-gray-200/50 shadow-sm shadow-gray-100/50 backdrop-blur-sm transition-all duration-300"
                        title="소개트리"
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                    >
                        <div className="p-1.5 rounded-lg bg-gray-200/60 group-hover:bg-gray-200/80 group-hover:scale-110 transition-all duration-300">
                            <Users className="w-3.5 h-3.5 text-gray-700" />
                        </div>
                        <span className="tracking-tight hidden md:inline">소개트리</span>
                    </motion.button>

                    {/* 즐겨찾기 버튼 */}
                    <motion.button
                        onClick={() => {
                            sendActionLog('즐겨찾기_열기');

                            // GA4: 즐겨찾기 버튼 클릭 이벤트 추적
                            try {
                                const gtag = window.gtag;
                                const measurementId = window.GA_MEASUREMENT_ID;
                                if (typeof gtag === 'function' && measurementId) {
                                    const deviceId = generateDeviceId();
                                    gtag('event', 'favorite_button_click', {
                                        button_location: 'main_navigation',
                                        event_category: 'navigation',
                                        event_label: '즐겨찾기 버튼 클릭',
                                        device_id: deviceId
                                    });
                                }
                            } catch (gaError) {
                                console.warn('GA4 이벤트 전송 실패:', gaError);
                            }

                            setShowFavoriteModal(true);
                        }}
                        className="group relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-medium text-xs bg-gradient-to-br from-gray-50 via-white to-gray-50/80 text-gray-800 border border-gray-300/60 hover:border-gray-400/80 hover:shadow-lg hover:shadow-gray-200/50 shadow-sm shadow-gray-100/50 backdrop-blur-sm transition-all duration-300"
                        title="즐겨찾기"
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                    >
                        <div className="p-1.5 rounded-lg bg-gray-200/60 group-hover:bg-gray-200/80 group-hover:scale-110 transition-all duration-300">
                            <Star className="w-3.5 h-3.5 text-gray-700" />
                        </div>
                        <span className="tracking-tight hidden md:inline">즐겨찾기</span>
                    </motion.button>

                </div>

                {/* 상단 배너형: 캐러셀 (오늘의 미션 / 이번달 목표 / 올해의 목표) */}
                <div className="mb-6">
                    <div className="relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-300">
                        {/* 좌우 네비게이션 */}
                        <button
                            onClick={() => setActiveBanner(prev => Math.max(0, prev - 1))}
                            className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/80 rounded-full shadow hover:bg-white"
                            aria-label="이전 배너"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-700" />
                        </button>
                        <button
                            onClick={() => setActiveBanner(prev => Math.min(2, prev + 1))}
                            className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/80 rounded-full shadow hover:bg-white"
                            aria-label="다음 배너"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-700" />
                        </button>

                        {/* 인디케이터 */}
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
                            {[0, 1, 2].map(i => (
                                <button
                                    key={i}
                                    onClick={() => setActiveBanner(i)}
                                    className={`w-2.5 h-2.5 rounded-full ${activeBanner === i ? 'bg-gray-800' : 'bg-gray-300'}`}
                                    aria-label={`배너 ${i + 1}`}
                                />
                            ))}
                        </div>

                        {/* 슬라이드 컨테이너 */}
                        <div
                            className="w-full"
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                        >
                            <div
                                className="flex transition-transform duration-300"
                                style={{ transform: `translateX(calc(${(-activeBanner * 100)}% + ${touchDeltaX}px))` }}
                            >
                                {/* 오늘의 미션 */}
                                <div className="min-w-full p-4 sm:p-5">
                                    <div className="flex flex-col gap-1 items-center">
                                        <div className="flex items-center gap-2 -mt-2 justify-center">
                                            <div className="flex items-center gap-2 mb-1">
                                                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">오늘의 미션</p>
                                                <button
                                                    onClick={openMissionEditor}
                                                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                                                    title="미션 설정"
                                                >
                                                    <Settings className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* 본문: 미션 */}
                                        <div className="flex flex-col gap-3 items-center">
                                            <div className="text-base text-gray-800 font-medium text-center mb-2">{missionText}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* 이번달 목표 */}
                                <div className="min-w-full p-4 sm:p-5">
                                    <div className="flex flex-col gap-1 items-center">
                                        <div className="flex items-center gap-2 -mt-2 justify-center">
                                            <div className="flex items-center gap-2 mb-1">
                                                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">이번달 목표</p>
                                                <button
                                                    onClick={() => { setTempMonthly(monthlyGoalText); setShowMonthlyEditor(true); }}
                                                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                                                    title="목표 설정"
                                                >
                                                    <Settings className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="text-base text-gray-800 font-medium text-center mb-2">{monthlyGoalText}</div>
                                    </div>
                                </div>

                                {/* 올해의 목표 */}
                                <div className="min-w-full p-4 sm:p-5">
                                    <div className="flex flex-col gap-1 items-center">
                                        <div className="flex items-center gap-2 -mt-2 justify-center">
                                            <div className="flex items-center gap-2 mb-1">
                                                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">올해의 목표</p>
                                                <button
                                                    onClick={() => { setTempYearly(yearlyGoalText); setShowYearlyEditor(true); }}
                                                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                                                    title="목표 설정"
                                                >
                                                    <Settings className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="text-base text-gray-800 font-medium text-center mb-2">{yearlyGoalText}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 단계별 고객 수 통계 - 클릭 가능 */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
                    {CUSTOMER_STAGES.map((stage, index) => {
                        const stageCustomers = customers[stage.id] || [];
                        const stageCount = stageCustomers.length;

                        return (
                            <button
                                key={stage.id}
                                onClick={() => scrollToStage(stage.id)}
                                className="group bg-white/60 backdrop-blur-lg p-4 rounded-2xl shadow-lg hover:shadow-xl border border-white/20 transition-all duration-300 hover:-translate-y-1 hover:scale-105 cursor-pointer"
                                title={`${stage.title} 섹션으로 이동`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{stage.title}</p>
                                        <p className="text-2xl font-bold text-gray-900">{stageCount}</p>
                                    </div>
                                    <div className={`w-10 h-10 ${stage.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                        <span className="text-lg">{stage.emoji}</span>
                                    </div>
                                </div>
                            </button>
                        );
                    })}

                    {/* 히스토리 카드 */}
                    <button
                        onClick={() => {
                            sendActionLog('히스토리_보기');
                            setShowHistoryModal(true);
                        }}
                        className="group bg-gradient-to-br from-gray-100 to-gray-200 backdrop-blur-lg p-4 rounded-2xl shadow-lg hover:shadow-xl border border-gray-300 transition-all duration-300 hover:-translate-y-1 hover:scale-105 cursor-pointer"
                        title="히스토리 기록 보기"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">히스토리</p>
                                {/* 지연 집계: 클릭 시 계산하도록 변경하여 초기 렌더 가볍게 */}
                                <p className="text-2xl font-bold text-gray-900">히스토리 보기</p>
                            </div>
                            <div className="w-10 h-10 bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                                <Clock className="w-5 h-5 text-white" />
                            </div>
                        </div>
                    </button>
                </div>

                {/* 스크롤 안내 배너 */}
                {showScrollBanner && (
                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl shadow-lg border border-white/20 p-4 mb-4 relative">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                    <ChevronDown className="w-5 h-5" />
                                </div>
                                <span className="font-medium">아래로 스크롤 해보세요</span>
                            </div>
                            <button
                                onClick={() => {
                                    setShowScrollBanner(false);
                                    try {
                                        localStorage.setItem('scrollBannerDismissed', 'true');
                                    } catch (_) { }
                                }}
                                className="p-1 rounded-lg hover:bg-white/20 transition-colors"
                                title="닫기"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* 고객명 검색 */}
                <div className="bg-white/60 backdrop-blur-lg rounded-2xl shadow-lg border border-white/20 p-4 mb-8">
                    <div className="flex items-center gap-3">
                        <Search className="w-5 h-5 text-gray-500" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    performSearch();
                                }
                            }}
                            placeholder="고객/메모/키워드/히스토리 검색..."
                            className="flex-1 bg-transparent outline-none text-gray-800 placeholder:text-gray-400"
                        />
                        <button
                            onClick={performSearch}
                            disabled={isSearching || !searchQuery.trim()}
                            className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            {isSearching ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <Search className="w-4 h-4" />
                            )}
                        </button>
                        {searchQuery && (
                            <button
                                onClick={() => {
                                    setSearchQuery('');
                                    setSearchResults([]);
                                }}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                title="지우기"
                            >
                                <X className="w-4 h-4 text-gray-500" />
                            </button>
                        )}
                    </div>
                    {searchResults.length > 0 && (
                        <div className="mt-3">
                            <div className="text-sm text-gray-600 mb-2">
                                검색 결과: {searchResults.length}명
                            </div>
                            <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 bg-white/70 rounded-xl border border-gray-200">
                                {searchResults.map((c) => {
                                    const q = (searchQuery || '').trim();
                                    // 스니펫: 히스토리 → 메모/키워드 → 주소 → 전화 순으로 최초 매칭 문장 추출
                                    let snippet = '';
                                    // 히스토리 스니펫
                                    if (!snippet) {
                                        const hs = historiesByCustomer[c.id] || [];
                                        for (const h of hs) {
                                            const text = [h?.note, h?.followUp?.content].filter(Boolean).join(' ');
                                            const idx = text.toLowerCase().indexOf(q.toLowerCase());
                                            if (idx >= 0) {
                                                const start = Math.max(0, idx - 15);
                                                snippet = text.slice(start, idx + q.length + 15);
                                                break;
                                            }
                                        }
                                    }
                                    const candidates = [c.memo, c.keyword, c.company, c.address, c.detailAddress, c.phone, c.relation];
                                    if (!snippet) {
                                        for (const t of candidates) {
                                            const s = String(t || '');
                                            const idx = s.toLowerCase().indexOf(q.toLowerCase());
                                            if (idx >= 0) {
                                                const start = Math.max(0, idx - 15);
                                                snippet = s.slice(start, idx + q.length + 15);
                                                break;
                                            }
                                        }
                                    }

                                    return (
                                        <button
                                            key={`${c.__stageId || c.stage}-${c.id}`}
                                            onClick={() => {
                                                // 검색 결과에서 고객을 클릭할 때, 현재 customers 객체에서 실제 단계를 찾아서 __stageId 설정
                                                // IndexedDB에서 가져온 데이터의 __stageId는 이전 단계일 수 있으므로 항상 customers에서 찾음
                                                let actualStageId = null;
                                                // customers 객체에서 고객이 현재 어느 단계에 있는지 찾기
                                                for (const [stageId, stageCustomers] of Object.entries(customers)) {
                                                    if (stageCustomers && stageCustomers.some(cust => cust.id === c.id)) {
                                                        actualStageId = stageId;
                                                        break;
                                                    }
                                                }
                                                // customers에서 찾지 못한 경우에만 IndexedDB 데이터의 stage 사용
                                                if (!actualStageId) {
                                                    actualStageId = c.stage;
                                                }
                                                // __stageId를 포함한 고객 객체로 설정
                                                setSelectedCustomer({ ...c, __stageId: actualStageId });
                                            }}
                                            className="w-full text-left px-4 py-2 hover:bg-gray-50"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-800 font-medium">{highlightText(c.name, q)}</span>
                                                {c.company && (
                                                    <span className="text-xs text-gray-500 ml-3">{highlightText(c.company, q)}</span>
                                                )}
                                            </div>
                                            {snippet && (
                                                <div className="text-[11px] text-gray-500 mt-1 line-clamp-1">
                                                    …{highlightText(snippet, q)}…
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* 체험중 배너 */}
                {appStatus === 'trial' && (
                    (() => {
                        try {
                            // 체험 남은 일수 계산
                            let daysLeft = null;
                            if (trialInfo && typeof trialInfo.daysRemaining === 'number') {
                                daysLeft = trialInfo.daysRemaining;
                            } else {
                                const trialStartDate = localStorage.getItem('trialStartDate');
                                if (trialStartDate) {
                                    daysLeft = calculateRemainingDays(trialStartDate);
                                } else {
                                    const v = localStorage.getItem('verificationDate');
                                    if (v) {
                                        daysLeft = calculateRemainingDays(v);
                                    }
                                }
                            }

                            if (daysLeft === null) return null;

                            return (
                                <motion.div
                                    className="bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg rounded-2xl mb-8"
                                    initial={{ y: -20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: -20, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className="text-2xl">⏰</div>
                                            <div>
                                                <div className="font-bold text-sm sm:text-base">체험 기간이 {daysLeft}일 남았습니다</div>
                                                <div className="text-xs sm:text-sm opacity-90">추가기기 사용 등 유료회원 전용 기능들도 사용해보세요</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => {
                                                    // GA4: 사용신청 버튼 클릭 이벤트 추적
                                                    try {
                                                        const gtag = window.gtag;
                                                        const measurementId = window.GA_MEASUREMENT_ID;
                                                        if (typeof gtag === 'function' && measurementId) {
                                                            const deviceId = generateDeviceId();
                                                            gtag('event', 'application_button_click', {
                                                                app_status: appStatus,
                                                                button_location: 'trial_banner',
                                                                event_category: 'application',
                                                                event_label: '사용신청 버튼 클릭',
                                                                device_id: deviceId
                                                            });
                                                        }
                                                    } catch (gaError) {
                                                        console.warn('GA4 이벤트 전송 실패:', gaError);
                                                    }

                                                    try {
                                                        window.dispatchEvent(new Event('openApprovalModal'));
                                                    } catch (e) {
                                                        console.warn('[TrialBanner] dispatch failed', e);
                                                    }
                                                }}
                                                className="px-4 py-2 bg-white text-red-600 rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
                                            >
                                                사용신청하기
                                            </button>
                                            <a
                                                href="http://pf.kakao.com/_XTTxoxj/chat"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-4 py-2 bg-[#FEE500] text-gray-800 rounded-lg font-bold text-sm hover:bg-[#FEE500]/90 transition-colors whitespace-nowrap"
                                            >
                                                카톡문의
                                            </a>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        } catch (_) {
                            return null;
                        }
                    })()
                )}

                {/* 체험만료 배너 */}
                {appStatus === 'expired' && (
                    <motion.div
                        className="bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg rounded-2xl mb-8"
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -20, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-3">
                                <div className="text-2xl">⏰</div>
                                <div>
                                    <div className="font-bold text-sm sm:text-base">체험 기간이 만료되었습니다</div>
                                    <div className="text-xs sm:text-sm opacity-90">고객 조회는 자유롭게 가능합니다</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        // GA4: 사용신청 버튼 클릭 이벤트 추적
                                        try {
                                            const gtag = window.gtag;
                                            const measurementId = window.GA_MEASUREMENT_ID;
                                            if (typeof gtag === 'function' && measurementId) {
                                                const deviceId = generateDeviceId();
                                                gtag('event', 'application_button_click', {
                                                    app_status: appStatus,
                                                    button_location: 'expired_banner',
                                                    event_category: 'application',
                                                    event_label: '사용신청 버튼 클릭',
                                                    device_id: deviceId
                                                });
                                            }
                                        } catch (gaError) {
                                            console.warn('GA4 이벤트 전송 실패:', gaError);
                                        }

                                        try {
                                            window.dispatchEvent(new Event('openApprovalModal'));
                                        } catch (e) {
                                            console.warn('[ExpiredBanner] dispatch failed', e);
                                        }
                                    }}
                                    className="px-4 py-2 bg-white text-red-600 rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
                                >
                                    사용신청하기
                                </button>
                                <a
                                    href="http://pf.kakao.com/_XTTxoxj/chat"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-2 bg-[#FEE500] text-gray-800 rounded-lg font-bold text-sm hover:bg-[#FEE500]/90 transition-colors whitespace-nowrap"
                                >
                                    카톡문의
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* 모던 고객 단계 카드들 또는 리스트 뷰 */}
                <div className={`grid grid-cols-1 ${showListView ? '' : 'lg:grid-cols-2 xl:grid-cols-3'} gap-6 transition-opacity duration-300 ${showListView ? 'opacity-100' : 'opacity-100'}`}>
                    {CUSTOMER_STAGES.map((stage, index) => {
                        const stageCustomers = customers[stage.id] || [];
                        const stageValue = stageCustomers.reduce((sum, customer) => sum + (customer.value || 0), 0);
                        const highPriorityInStage = stageCustomers.filter(c => c.priority === 'high').length;

                        // 리스트 뷰: 모든 고객을 합쳐 이름 기준 정렬하여 단일 리스트로 표출
                        if (showListView) {
                            return null; // 리스트는 아래에서 한 번만 렌더링
                        }

                        return (
                            <div
                                key={stage.id}
                                ref={stageRefs.current[stage.id]}
                                className="group bg-white/60 backdrop-blur-lg rounded-2xl shadow-lg hover:shadow-xl border border-white/20 transition-all duration-300 hover:-translate-y-2 overflow-hidden"
                            >
                                {/* 카드 헤더 */}
                                <div className={`${stage.lightColor} border-b border-gray-200 p-6`}>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-12 h-12 ${stage.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                                <span className="text-2xl">{stage.emoji}</span>
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-800">{stage.title}</h3>
                                                <p className="text-gray-600 text-sm">{stage.subtitle}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {/* Selection Mode Buttons */}
                                            {selectionModeStageId === stage.id ? (
                                                <>
                                                    <button
                                                        onClick={() => toggleSelectionMode(stage.id)}
                                                        className="px-3 py-2 bg-gray-100 text-gray-600 rounded-xl text-xs font-bold hover:bg-gray-200"
                                                    >
                                                        취소
                                                    </button>
                                                    <button
                                                        onClick={handleBulkMoveClick}
                                                        disabled={selectedCustomerIds.size === 0}
                                                        className="px-3 py-2 bg-purple-500 text-white rounded-xl text-xs font-bold hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        이동 ({selectedCustomerIds.size})
                                                    </button>
                                                    <button
                                                        onClick={() => handleSelectAll(stage.id, stageCustomers)}
                                                        className="px-3 py-2 bg-blue-100 text-blue-600 rounded-xl text-xs font-bold hover:bg-blue-200"
                                                    >
                                                        {selectedCustomerIds.size === stageCustomers.length ? '해제' : '전체'}
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={() => toggleSelectionMode(stage.id)}
                                                    className="p-2 bg-white/80 rounded-xl hover:bg-white shadow-md hover:shadow-lg transition-all duration-300"
                                                    title="다중 선택"
                                                    disabled={selectionModeStageId !== null} // Disable if other stage is selecting
                                                >
                                                    <ClipboardCheck className="w-5 h-5 text-gray-600 hover:text-purple-600 transition-colors duration-300" />
                                                </button>
                                            )}

                                            <button
                                                onClick={() => toggleSortByName(stage.id)}
                                                className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-300 hover:scale-105 ${sortByName[stage.id] !== false
                                                    ? 'bg-blue-100 text-blue-600 shadow-md'
                                                    : 'bg-white/80 text-gray-600 hover:bg-white shadow-md hover:shadow-lg'
                                                    }`}
                                                title={sortByName[stage.id] !== false ? '등록일순으로 변경' : '가나다순으로 변경'}
                                            >
                                                {sortByName[stage.id] !== false ? (
                                                    <>
                                                        <SortAsc className="w-4 h-4" />
                                                        <span className="text-xs font-medium">가나다순</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Calendar className="w-4 h-4" />
                                                        <span className="text-xs font-medium">등록일순</span>
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleShowAddForm(stage.id)}
                                                className={`p-2 bg-white/80 rounded-xl hover:bg-white shadow-md hover:shadow-lg transition-all duration-300 hover:scale-110 ${appStatus === 'expired' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                title={appStatus === 'expired' ? '체험 기간이 만료되었습니다. 사용신청을 해주세요.' : `${stage.title}에 고객 추가`}
                                                disabled={appStatus === 'expired'}
                                            >
                                                <Plus className="w-5 h-5 text-gray-600 hover:text-blue-600 transition-colors duration-300" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* 단계별 통계 */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-gray-800">{stageCustomers.length}</div>
                                            <div className="text-xs text-gray-600 font-medium">총 고객</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-lg font-bold text-green-600">{formatMoney(stageValue)}</div>
                                            <div className="text-xs text-gray-600 font-medium">예상 가치</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xl font-bold text-red-600">{highPriorityInStage}</div>
                                            <div className="text-xs text-gray-600 font-medium">우선 고객</div>
                                        </div>
                                    </div>
                                </div>

                                {/* 고객 목록 */}
                                <div className="p-6">
                                    {stageCustomers.length === 0 ? (
                                        <div className="text-center py-8">
                                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <Users className="w-8 h-8 text-gray-400" />
                                            </div>
                                            <p className="text-gray-500 font-medium mb-3">아직 고객이 없습니다</p>
                                            <button
                                                onClick={() => handleShowAddForm(stage.id)}
                                                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-xl text-sm font-bold hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/25 transition-all duration-300 hover:scale-105"
                                                title={appStatus === 'expired' ? '누적 10명 제한' : ''}
                                            >
                                                첫 고객 추가하기
                                            </button>
                                        </div>
                                    ) : (
                                        <OptimizedStageList
                                            stageId={stage.id}
                                            customers={stageCustomers}
                                            visibleCounts={visibleCounts}
                                            onLoadMore={handleLoadMore}
                                            onItemClick={(customer) => handleCustomerClick({ ...customer, __stageId: stage.id })}
                                            sortByName={sortByName[stage.id] !== false}
                                            selectionMode={selectionModeStageId === stage.id}
                                            selectedIds={selectedCustomerIds}
                                            onToggleSelection={toggleCustomerSelection}
                                        />
                                    )}
                                </div>

                                {/* 단계별 액션 버튼 */}
                                {stageCustomers.length > 0 && (
                                    <div className="px-6 pb-6">
                                        <button
                                            onClick={() => handleShowAddForm(stage.id)}
                                            className="w-full bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 py-3 px-4 rounded-xl font-bold hover:from-gray-200 hover:to-gray-300 transition-all duration-300 flex items-center justify-center gap-2"
                                            title={appStatus === 'expired' ? '누적 10명 제한' : ''}
                                        >
                                            <Plus className="w-4 h-4" />
                                            고객 추가
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {showListView && (
                    (() => {
                        // 모든 고객 평탄화
                        const all = Object.entries(customers).flatMap(([stageId, list]) => (list || []).map(c => ({ ...c, __stageId: stageId })));
                        
                        // 정렬 로직
                        const sorted = [...all].sort((a, b) => {
                            let result;
                            if (listViewSortBy === 'createdAt') {
                                // 등록일순 정렬
                                const dateA = new Date(a.createdAt || 0);
                                const dateB = new Date(b.createdAt || 0);
                                result = dateB - dateA; // 기본값: 최신순 (내림차순)
                            } else {
                                // 이름순 정렬 (가나다순)
                                const collator = new Intl.Collator('ko-KR', { sensitivity: 'base', numeric: true });
                                result = collator.compare(a.name || '', b.name || ''); // 기본값: 가나다순 (오름차순)
                            }
                            // 정렬 방향 적용
                            return listViewSortOrder === 'asc' ? -result : result;
                        });
                        
                        return (
                            <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-lg border border-white/20 overflow-hidden transition-opacity duration-300">
                                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="text-sm font-bold text-gray-700">리스트 뷰</div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => setListViewSortBy('name')}
                                                className={`px-2 py-1 text-xs rounded-md transition-colors ${
                                                    listViewSortBy === 'name'
                                                        ? 'bg-blue-100 text-blue-700 font-semibold'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                                title="가나다순 정렬"
                                            >
                                                가나다
                                            </button>
                                            <button
                                                onClick={() => setListViewSortBy('createdAt')}
                                                className={`px-2 py-1 text-xs rounded-md transition-colors ${
                                                    listViewSortBy === 'createdAt'
                                                        ? 'bg-blue-100 text-blue-700 font-semibold'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                                title="등록일순 정렬"
                                            >
                                                등록일
                                            </button>
                                            <button
                                                onClick={() => setListViewSortOrder(listViewSortOrder === 'asc' ? 'desc' : 'asc')}
                                                className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-1"
                                                title={listViewSortOrder === 'asc' ? '내림차순으로 변경' : '오름차순으로 변경'}
                                            >
                                                {listViewSortOrder === 'asc' ? (
                                                    <>
                                                        <ArrowUpDown className="w-3 h-3" />
                                                        오름차순
                                                    </>
                                                ) : (
                                                    <>
                                                        <ArrowUpDown className="w-3 h-3 rotate-180" />
                                                        내림차순
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-500">총 {sorted.length}명</div>
                                </div>
                                <div className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto">
                                    {sorted.length === 0 && (
                                        <div className="p-6 text-center text-gray-500">표시할 고객이 없습니다</div>
                                    )}
                                    {sorted.map(c => (
                                        <button
                                            key={`${c.__stageId}-${c.id}`}
                                            onClick={() => handleCustomerClick(c)}
                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between"
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="font-medium text-gray-800">{c.name}</span>
                                                {(() => {
                                                    const stageInfo = CUSTOMER_STAGES.find(s => s.id === c.__stageId);
                                                    return stageInfo ? (
                                                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded-md">
                                                            <span className={`w-4 h-4 ${stageInfo.color} rounded-sm flex items-center justify-center`}>{stageInfo.emoji}</span>
                                                            <span>{stageInfo.title}</span>
                                                        </span>
                                                    ) : null;
                                                })()}
                                            </div>
                                            <div className="text-xs text-gray-500">{c.phone}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })()
                )}

                {/* 고객 액션 오버레이 모달 */}
                {openingCustomerId && (
                    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4 z-40">
                        <div className="bg-white/90 rounded-xl px-5 py-4 text-sm text-gray-700 shadow-lg border border-gray-200">
                            고객 정보를 여는 중...
                        </div>
                    </div>
                )}
                {selectedCustomer && (
                    <div
                        className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4 z-[1002]"
                    >
                        <div
                            className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-sm md:max-w-2xl lg:max-w-3xl w-full max-h-[90vh] shadow-2xl border border-white/20 overflow-hidden flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* 오버레이 헤더 */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between mb-4">
                                    <div></div>
                                    <button
                                        onClick={() => setSelectedCustomer(null)}
                                        className="p-2 bg-white/60 rounded-xl hover:bg-white/80 transition-all duration-300"
                                        title="닫기"
                                    >
                                        <X className="w-5 h-5 text-gray-600" />
                                    </button>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-xl font-bold text-gray-800 mb-1">
                                        {selectedCustomer.name}
                                    </h3>
                                    {/* 현재 영업단계 배지 */}
                                    {(() => {
                                        // customers 객체에서 고객이 현재 어느 단계에 있는지 먼저 확인 (가장 정확한 정보)
                                        let guessedStageId = Object.keys(customers).find(stageId =>
                                            (customers[stageId] || []).some(c => c.id === selectedCustomer.id)
                                        );
                                        // customers에서 찾지 못한 경우에만 __stageId 사용
                                        if (!guessedStageId) {
                                            guessedStageId = selectedCustomer.__stageId;
                                        }
                                        const stageInfo = CUSTOMER_STAGES.find(s => s.id === guessedStageId);
                                        return stageInfo ? (
                                            <div className="mt-1 inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-bold bg-white border border-gray-200 shadow-sm">
                                                <span className={`w-5 h-5 ${stageInfo.color} rounded-md flex items-center justify-center`}>{stageInfo.emoji}</span>
                                                <span className="text-gray-700">{stageInfo.title}</span>
                                            </div>
                                        ) : null;
                                    })()}
                                    {selectedCustomer.priority === 'high' && (
                                        <span className="bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1 rounded-lg text-sm font-bold shadow-md">
                                            우선 고객
                                        </span>
                                    )}
                                    {(() => {
                                        // customers 객체에서 고객이 현재 어느 단계에 있는지 확인
                                        let guessedStageId = Object.keys(customers).find(stageId =>
                                            (customers[stageId] || []).some(c => c.id === selectedCustomer.id)
                                        );
                                        if (!guessedStageId) {
                                            guessedStageId = selectedCustomer.__stageId;
                                        }
                                        return guessedStageId === 'preparation' && selectedCustomer.recommended ? (
                                            <span 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const recommendedDate = selectedCustomer.recommendedDate 
                                                        ? new Date(selectedCustomer.recommendedDate).toLocaleDateString('ko-KR', { 
                                                            year: 'numeric', 
                                                            month: 'long', 
                                                            day: 'numeric' 
                                                        })
                                                        : '최근';
                                                    alert(`이 고객은 고객뱅크에서 일일 추천으로 선택되어\n연락대상 단계로 이동되었습니다.\n\n추천일: ${recommendedDate}`);
                                                }}
                                                className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 py-1 rounded-lg text-sm font-bold shadow-md cursor-pointer hover:from-purple-600 hover:to-purple-700 transition-all duration-200"
                                                title="클릭하면 추천 이유를 확인할 수 있습니다"
                                            >
                                                추천
                                            </span>
                                        ) : null;
                                    })()}
                                    {selectedCustomer.company && (
                                        <p className="text-gray-600 text-sm mt-2">{highlightText(selectedCustomer.company, searchQuery)}</p>
                                    )}
                                    {selectedCustomer.relation && (
                                        <div className="flex items-center justify-center gap-2 text-gray-600 text-sm mt-1">
                                            <Users className="w-4 h-4" />
                                            <span>관계: {highlightText(selectedCustomer.relation, searchQuery)}</span>
                                        </div>
                                    )}
                                    <p className="text-gray-600 text-sm mt-1">{highlightText(selectedCustomer.phone, searchQuery)}</p>
                                    {selectedCustomer.birthdate && (
                                        <p className="text-gray-600 text-sm mt-1">생년월일: {highlightText(selectedCustomer.birthdate, searchQuery)}</p>
                                    )}
                                    {selectedCustomer.keyword && (
                                        <div className="flex items-center justify-center gap-2 text-gray-600 text-sm mt-1">
                                            <Tag className="w-4 h-4" />
                                            <span>{highlightText(selectedCustomer.keyword, searchQuery)}</span>
                                        </div>
                                    )}
                                    {selectedCustomer.value > 0 && (
                                        <p className="text-green-600 font-bold text-lg mt-2">
                                            {formatMoney(selectedCustomer.value)}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* 스크롤 가능한 메인 콘텐츠 */}
                            <div className="flex-1 overflow-y-auto">
                                <div className="p-6">
                                    <div className="grid grid-cols-4 gap-3 mb-4">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCall(selectedCustomer.phone);
                                            }}
                                            className="flex items-center justify-center bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-4 rounded-xl font-bold hover:from-green-600 hover:to-green-700 shadow-lg shadow-green-500/25 transition-all duration-300 hover:scale-105"
                                        >
                                            <Phone className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!isMobileDevice()) {
                                                    try { alert('모바일에서만 동작하는 기능입니다'); } catch (_) { }
                                                    return;
                                                }
                                                setShowKakaoModal(true);
                                            }}
                                            className="flex items-center justify-center bg-gradient-to-r from-yellow-400 to-yellow-500 text-white py-4 px-4 rounded-xl font-bold hover:from-yellow-500 hover:to-yellow-600 shadow-lg shadow-yellow-500/25 transition-all duration-300 hover:scale-105"
                                        >
                                            <MessageCircle className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditCustomer(selectedCustomer);
                                                closeOverlay();
                                            }}
                                            className="flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 px-4 rounded-xl font-bold hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/25 transition-all duration-300 hover:scale-105"
                                            title={appStatus === 'expired' ? '누적 10명 제한' : ''}
                                        >
                                            <Edit className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteCustomer(selectedCustomer);
                                                closeOverlay();
                                            }}
                                            className="flex items-center justify-center bg-gradient-to-r from-red-500 to-red-600 text-white py-4 px-4 rounded-xl font-bold hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-500/25 transition-all duration-300 hover:scale-105"
                                            title={appStatus === 'expired' ? '누적 10명 제한' : ''}
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>

                                    {/* 3열: 단계이동하기 단독 버튼 */}
                                    <div className="mt-3">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onMoveCustomer(selectedCustomer);
                                                // 단계 이동 모달이 열릴 때는 고객 상세 모달을 닫지 않음
                                            }}
                                            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white py-4 px-4 rounded-xl font-bold hover:from-purple-600 hover:to-purple-700 shadow-lg shadow-purple-500/25 transition-all duration-300 hover:scale-105"
                                        >
                                            <ArrowRight className="w-5 h-5" />
                                            단계이동하기
                                        </button>
                                    </div>

                                    {/* 메모 표시 */}
                                    {selectedCustomer.memo && (
                                        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                                            <h4 className="text-sm font-bold text-blue-800 mb-1">메모</h4>
                                            <p className="text-blue-700 text-sm whitespace-pre-line">{highlightText(selectedCustomer.memo, searchQuery)}</p>
                                        </div>
                                    )}

                                    {/* 히스토리 */}
                                    <CustomerHistorySection key={selectedCustomer.id} customerId={selectedCustomer.id} highlightQuery={searchQuery} appStatus={appStatus} customer={selectedCustomer} />

                                </div>
                            </div>

                            {/* 고정된 닫기 버튼 */}
                            <div className="p-6 border-t border-gray-200 bg-white/95">
                                <button
                                    onClick={closeOverlay}
                                    className="w-full py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-all duration-300"
                                >
                                    닫기
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 히스토리 모달 */}
            {showKakaoModal && (
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 1200 }} onClick={() => setShowKakaoModal(false)}>
                    <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full shadow-2xl border border-white/20 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-xl bg-yellow-100 border border-yellow-200 flex items-center justify-center">
                                    <MessageCircle className="w-5 h-5 text-yellow-700" />
                                </div>
                                <h3 className="text-base font-bold text-gray-800">카카오톡 메시지 보내기</h3>
                            </div>
                            <button onClick={() => setShowKakaoModal(false)} className="p-2 rounded-lg hover:bg-gray-100">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="text-sm text-gray-600">{selectedCustomer?.name}님에게 보낼 메시지를 입력하세요.</div>
                            <textarea
                                value={kakaoMessage}
                                onChange={(e) => setKakaoMessage(e.target.value)}
                                placeholder="메시지 내용을 입력하세요"
                                rows="6"
                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-yellow-500 transition-all duration-300 resize-y"
                            />
                            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                                💡 <strong>안내:</strong> '전송'버튼을 누르면 카카오톡 앱을 선택할 수 있습니다. 수신대상은 카톡에서 직접 설정하시면 됩니다.
                            </div>
                        </div>
                        <div className="p-5 border-t border-gray-100 flex gap-3">
                            <button
                                onClick={() => setShowKakaoModal(false)}
                                className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all duration-300"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleSendKakao}
                                className="flex-1 py-3 px-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-xl font-bold hover:from-yellow-500 hover:to-yellow-600 shadow-lg shadow-yellow-500/25 transition-all duration-300 hover:scale-105"
                            >
                                전송
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showHistoryModal && (
                <React.Suspense fallback={
                    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                        <div className="bg-white/90 rounded-xl px-5 py-4 text-sm text-gray-700 shadow-lg border border-gray-200">기록을 불러오는 중...</div>
                    </div>
                }>
                    <HistoryModal
                        customers={customers}
                        onClose={() => setShowHistoryModal(false)}
                        onNavigateToStage={handleNavigateToStage}
                        onMoveCustomer={onMoveCustomer}
                        onSelectCustomer={handleSelectCustomerFromHistory}
                    />
                </React.Suspense>
            )}
            {showAudiobookModal && (
                <React.Suspense fallback={
                    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                        <div className="bg-white/90 rounded-xl px-5 py-4 text-sm text-gray-700 shadow-lg border border-gray-200">오디오북을 불러오는 중...</div>
                    </div>
                }>
                    <AudiobookPlayerModal
                        isOpen={showAudiobookModal}
                        onClose={async () => {
                            setShowAudiobookModal(false);
                            // 모달이 닫힐 때 배지 제거 및 오디오북 재확인
                            if (audiobook?.id) {
                                localStorage.setItem('lastCheckedAudiobookId', audiobook.id);
                                setHasNewAudiobook(false);
                            }
                            // 오디오북이 변경되었을 수 있으므로 다시 확인
                            try {
                                const audiobookData = await getActiveAudiobook();
                                if (audiobookData && audiobookData.downloadURL) {
                                    const lastCheckedAudiobookId = localStorage.getItem('lastCheckedAudiobookId');
                                    if (lastCheckedAudiobookId !== audiobookData.id) {
                                        setHasNewAudiobook(true);
                                    } else {
                                        setHasNewAudiobook(false);
                                    }
                                }
                            } catch (e) {
                                console.error('오디오북 재확인 실패:', e);
                            }
                        }}
                    />
                </React.Suspense>
            )}
            {showFavoriteModal && (
                <React.Suspense fallback={
                    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                        <div className="bg-white/90 rounded-xl px-5 py-4 text-sm text-gray-700 shadow-lg border border-gray-200">즐겨찾기를 불러오는 중...</div>
                    </div>
                }>
                    <FavoriteModal onClose={() => setShowFavoriteModal(false)} />
                </React.Suspense>
            )}

            {/* 미션 설정 모달 */}
            {showMissionEditor && (
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowMissionEditor(false)}>
                    <div className="bg-white/95 rounded-2xl w-full max-w-md shadow-2xl border border-white/20 p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="mb-4">
                            <h3 className="text-lg font-bold text-gray-800">오늘의 미션 설정</h3>
                            <p className="text-sm text-gray-500 mt-1">업무 흐름에 맞는 짧은 문구로 설정하세요.</p>
                        </div>
                        <input
                            type="text"
                            value={tempMission}
                            onChange={(e) => setTempMission(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder="예: 고객 5명에게 안부 메시지 보내기"
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <button className="px-4 py-2 rounded-lg bg-blue-600 text-white" onClick={saveMission}>저장</button>
                            <button className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700" onClick={() => setShowMissionEditor(false)}>취소</button>

                        </div>
                    </div>
                </div>
            )}

            {/* 이번달 목표 설정 모달 */}
            {showMonthlyEditor && (
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowMonthlyEditor(false)}>
                    <div className="bg-white/95 rounded-2xl w-full max-w-md shadow-2xl border border-white/20 p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="mb-4">
                            <h3 className="text-lg font-bold text-gray-800">이번달 목표 설정</h3>
                            <p className="text-sm text-gray-500 mt-1">이번달에 반드시 이루고 싶은 목표를 적어주세요.</p>
                        </div>
                        <input
                            type="text"
                            value={tempMonthly}
                            onChange={(e) => setTempMonthly(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder="예: 계약 3건 달성, 고객 50명 접촉"
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <button className="px-4 py-2 rounded-lg bg-blue-600 text-white" onClick={saveMonthlyGoal}>저장</button>
                            <button className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700" onClick={() => setShowMonthlyEditor(false)}>취소</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 올해의 목표 설정 모달 */}
            {showYearlyEditor && (
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowYearlyEditor(false)}>
                    <div className="bg-white/95 rounded-2xl w-full max-w-md shadow-2xl border border-white/20 p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="mb-4">
                            <h3 className="text-lg font-bold text-gray-800">올해의 목표 설정</h3>
                            <p className="text-sm text-gray-500 mt-1">1년 동안 달성하고 싶은 가장 중요한 목표를 적어주세요.</p>
                        </div>
                        <input
                            type="text"
                            value={tempYearly}
                            onChange={(e) => setTempYearly(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder="예: 연간 매출 1억원 달성, 고객 500명 관리"
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <button className="px-4 py-2 rounded-lg bg-blue-600 text-white" onClick={saveYearlyGoal}>저장</button>
                            <button className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700" onClick={() => setShowYearlyEditor(false)}>취소</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 오디오 엘리먼트 (숨김) */}
            <audio ref={audioRef} />
        </div>
    );
};

export default CustomerOverview;